import { useMutation } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '@reflex/ui/components/ui/button';
import { Input } from '@reflex/ui/components/ui/input';
import { Label } from '@reflex/ui/components/ui/label';
import {
  InlineDocsHint,
  PageTitle,
  PageDescription,
} from '@reflex/ui/components/layout/Typography';
import { toast } from 'sonner';
import { useAuth } from '@/hooks/auth/useAuth';
import { api } from '@/lib/api';
import { docs } from '../onboardingDocs';

const organizationDetailsSchema = z.object({
  name: z.string().min(1, 'Organization name is required'),
  supportEmail: z.string().email('Must be a valid email').or(z.literal('')),
});

type OrganizationDetailsValues = z.infer<typeof organizationDetailsSchema>;

/**
 * Editable organization details rendered when the wizard is in `existing`
 * mode and the user steps back to the (already done) `organization` step
 * on the rail. Re-running the create form here would either fail with a
 * slug collision or silently create a duplicate org, so this edits the
 * existing org in place instead: name and support email are fixable,
 * while the slug stays read-only (URLs and bookmarks already carry it).
 * Continue saves any pending edits before advancing.
 */
export function OrganizationDetailsStage({
  org,
  onContinue,
}: {
  org: { id: string; name: string; slug: string; supportEmail?: string | null };
  onContinue: () => void;
}) {
  const auth = useAuth();
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm<OrganizationDetailsValues>({
    resolver: zodResolver(organizationDetailsSchema),
    defaultValues: {
      name: org.name,
      supportEmail: org.supportEmail ?? '',
    },
  });

  const mutation = useMutation({
    mutationFn: (data: OrganizationDetailsValues) =>
      api.updateOrganization(org.id, {
        name: data.name,
        supportEmail: data.supportEmail || null,
      }),
    onSuccess: async (updated) => {
      toast.success('Organization updated');
      // The org name renders from `auth.memberships`; refresh before
      // advancing so the rest of the wizard shows the edited values.
      await auth.refreshSession();
      reset({ name: updated.name, supportEmail: updated.supportEmail ?? '' });
      onContinue();
    },
    onError: (err: Error) =>
      toast.error('Failed to update organization', { description: err.message }),
  });

  return (
    <>
      <PageTitle docs={docs('stage-1-organization')}>Organization details</PageTitle>
      <PageDescription>
        <strong>{org.name}</strong> is created. Adjust anything you'd like to change, then continue.
      </PageDescription>
      <form
        onSubmit={handleSubmit((data) => {
          if (!isDirty) {
            onContinue();
            return;
          }
          mutation.mutate(data);
        })}
        className="mt-6 space-y-4"
      >
        <div className="space-y-2">
          <Label htmlFor="org-edit-name">
            Organization name <InlineDocsHint slug={docs('organization-name')} />
          </Label>
          <Input id="org-edit-name" data-testid="org-setup-edit-name" {...register('name')} />
          {errors.name && <p className="text-xs text-destructive">{errors.name.message}</p>}
        </div>
        <div className="space-y-2">
          <Label htmlFor="org-edit-slug">
            URL slug <InlineDocsHint slug={docs('url-slug')} />
          </Label>
          <Input
            id="org-edit-slug"
            data-testid="org-setup-edit-slug"
            value={org.slug}
            readOnly
            disabled
          />
          <p className="text-xs text-muted-foreground">
            The slug is part of every link to this organization and can't be changed here.
          </p>
        </div>
        <div className="space-y-2">
          <Label htmlFor="org-edit-support-email">
            Support email (optional) <InlineDocsHint slug={docs('support-email')} />
          </Label>
          <Input
            id="org-edit-support-email"
            type="email"
            data-testid="org-setup-edit-support-email"
            {...register('supportEmail')}
          />
          {errors.supportEmail && (
            <p className="text-xs text-destructive">{errors.supportEmail.message}</p>
          )}
        </div>
        <div className="flex items-center justify-end border-t border-border/40 pt-4">
          <Button
            type="submit"
            disabled={mutation.isPending}
            data-testid="org-setup-organization-continue"
          >
            {mutation.isPending ? 'Saving…' : isDirty ? 'Save and continue' : 'Continue'}
          </Button>
        </div>
      </form>
    </>
  );
}
